import { useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "@/i18n";
import { Label } from "@/components/ui/label";
import { CommandSelect, type CommandSelectOption } from "@/components/ui/command-select";
import { useStore } from "@/stores/store-context";

/** Split the stored "CC" / "CC-SUB" string into its two halves. */
export function splitRegion(region: string | undefined): { country: string; subdivision: string } {
  const [country = "", subdivision = ""] = (region ?? "").split("-");
  return { country, subdivision };
}

/** Join the two halves back into the stored format. A subdivision without a
 *  country is meaningless, so it is dropped rather than emitted bare. */
export function joinRegion(country: string, subdivision: string): string {
  if (!country) return "";
  return subdivision ? `${country}-${subdivision}` : country;
}

interface CountryRegionPickerProps {
  /** Combined region string ("CC" / "CC-SUB"), mirroring the backend format. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Prefixes the two selects' ids so labels bind uniquely per surface. */
  idPrefix: string;
  /** Overrides the default per-user help text (e.g. the account-default copy). */
  helpText?: string;
  /** Drop the help line entirely, for a surface whose own copy already explains
   *  the field (the holiday-calendar scope editor says it above the row). */
  hideHelp?: boolean;
}

/**
 * Country + dependent Region pickers over the shared ISO-3166 catalogue served by
 * the backend (`calendar/holiday_regions.py` — the catalogue lives there for
 * historical reasons; it is not holiday-specific). Emits the combined "CC" /
 * "CC-SUB" string the account default (M1) and the per-user `country_region`
 * field (M2, #3971) both store, so every surface picks a region the same way.
 * Deliberately purpose-neutral: holiday calendars are the first consumer of this
 * attribute, not the only expected one.
 *
 * **Built on `CommandSelect`, not `Select`, because the country list is the whole
 * ISO-3166 catalogue** — roughly 250 entries, and some countries carry another
 * 50+ subdivisions. A plain `Select` offers only a scrollbar for that, which is
 * what NG-545 reported: picking "United Kingdom" meant scrolling past two hundred
 * countries. `CommandSelect` is the documented primitive for exactly this case
 * ("the scaling case `Select` explicitly can't cover") and renders the selected
 * option's own label, so the trigger can never fall back to the raw code.
 *
 * That last part is also why this component exists at all. A second, older
 * `CountryRegionSelect` used to sit beside it on a bare `<SelectValue />`, which
 * base-ui renders as the raw `value` — so every holiday-calendar surface showed
 * "DZ" and "BC" where this one showed "Algeria" and "British Columbia" (NG-543).
 * Two components for one job meant one of them carried the fix. Don't add a third:
 * every surface that needs a country and region uses this.
 */
export const CountryRegionPicker = observer(function CountryRegionPicker({
  value,
  onChange,
  disabled,
  idPrefix,
  helpText,
  hideHelp = false,
}: CountryRegionPickerProps) {
  const { t } = useTranslation();
  const { workspaceCalendars } = useStore();
  const { country, subdivision } = splitRegion(value);

  useEffect(() => {
    void workspaceCalendars.fetchHolidayCountries();
  }, [workspaceCalendars]);

  const countries = workspaceCalendars.holidayCountries;
  const selectedCountry = useMemo(
    () => countries.find((item) => item.code === country),
    [countries, country],
  );
  const hasSubdivisions = Boolean(selectedCountry?.subdivisions.length);

  const countryOptions = useMemo<CommandSelectOption[]>(
    // The code rides along as a keyword so typing "CA" finds Canada, the way
    // someone who knows the ISO code expects — the label alone wouldn't match.
    () => countries.map((item) => ({ id: item.code, name: item.label, keywords: [item.code] })),
    [countries],
  );

  const subdivisionOptions = useMemo<CommandSelectOption[]>(
    () =>
      (selectedCountry?.subdivisions ?? []).map((item) => ({
        id: item.code,
        name: item.label,
        keywords: [item.code],
      })),
    [selectedCountry],
  );

  return (
    <div className="space-y-2">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-country`}>{t("country_region.country_label")}</Label>
          <CommandSelect
            mode="single"
            id={`${idPrefix}-country`}
            options={countryOptions}
            value={country || null}
            // Changing country drops any stale subdivision — it belongs to the
            // country being replaced, and a "CA-BC" left under "France" would be
            // written straight back to the server.
            onChange={(next) => onChange(next ?? "")}
            disabled={disabled}
            placeholder={t("country_region.country_placeholder")}
            searchPlaceholder={t("country_region.country_search_placeholder")}
            emptyLabel={t("country_region.country_empty")}
            triggerAriaLabel={t("country_region.country_label")}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-region`}>{t("country_region.region_label")}</Label>
          <CommandSelect
            mode="single"
            id={`${idPrefix}-region`}
            options={subdivisionOptions}
            value={subdivision || null}
            onChange={(next) => onChange(joinRegion(country, next ?? ""))}
            disabled={disabled || !hasSubdivisions}
            // Region is optional, and most of the catalogue's countries have no
            // subdivisions at all — so an unset region says so plainly rather
            // than inviting a choice that isn't there.
            placeholder={
              country ? t("country_region.region_none") : t("country_region.region_placeholder")
            }
            searchPlaceholder={t("country_region.region_search_placeholder")}
            emptyLabel={t("country_region.region_empty")}
            triggerAriaLabel={t("country_region.region_label")}
            // The base's own clear row, rather than the sentinel option this
            // component used to carry (NG-1017): picking a region and changing
            // your mind has to be reachable, and a country-wide calendar is a
            // real choice, not an absence.
            clearable
            clearLabel={t("country_region.region_none")}
          />
        </div>
      </div>
      {hideHelp ? null : (
        <p className="text-sm text-muted-foreground">{helpText ?? t("country_region.help")}</p>
      )}
    </div>
  );
});
